import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { SearchScope } from 'owa-search-service/lib/data/schema/SearchScope';
import { SearchScopeKind } from 'owa-search-service/lib/data/schema/SearchScope';
import { SearchProvider } from 'owa-search-service/lib/data/schema/SearchProvider';
import { isOffline } from 'owa-offline/lib/isOffline';
import { isOfflineSyncEnabled, isOfflineSearchEnabled } from 'owa-offline-sync-feature-flags';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { logUsage } from 'owa-analytics';
import { getScenarioStore, SearchScenarioId } from 'owa-search-store';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { isAccountExplicitLogon } from 'owa-account-shared-mailbox-utils';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';

// Cache to store search providers for each search session
// Exported so tests can access it
export const searchProviderCache: Map<string, SearchProvider> = new Map();

export default function getSearchProvider(
    searchScope: SearchScope,
    isOfflineFallback?: boolean
): SearchProvider {
    const scopeKind = searchScope?.kind;
    const searchSessionGuid = getScenarioStore(SearchScenarioId.Mail).searchSessionGuid;
    const shouldWriteToCache = !!(scopeKind && searchSessionGuid);
    const mailboxInfo = getModuleContextMailboxInfo();

    // If an online search has fallen back to offline search, return offline provider
    if (isOfflineFallback) {
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, SearchProvider.Offline);
        }
        return SearchProvider.Offline;
    }

    if (searchProviderCache.has(searchSessionGuid)) {
        const searchProviderFromCache = searchProviderCache.get(searchSessionGuid);
        if (searchProviderFromCache) {
            return searchProviderFromCache;
        }
    }

    if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
        const searchProvider = SearchProvider.UnifiedInbox;
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, searchProvider);
        }
        return searchProvider;
    }

    if (scopeKind === SearchScopeKind.PstFile || scopeKind === SearchScopeKind.AllPstFiles) {
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, SearchProvider.PST);
        }
        return SearchProvider.PST;
    }

    if (scopeKind == SearchScopeKind.MetaOSApp) {
        const searchProvider = SearchProvider.SubstrateV2;
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, searchProvider);
        }
        return searchProvider;
    }

    if (
        (isOfflineSearchEnabled(mailboxInfo) && isOffline()) ||
        isGlobalFeatureEnabled('sea-localSearchOverride')
    ) {
        const searchProvider = SearchProvider.Offline;
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, searchProvider);
        }
        return searchProvider;
    }

    if (scopeKind === SearchScopeKind.SharedFolders) {
        const searchProvider = SearchProvider.SubstrateV2;
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, searchProvider);
        }
        return searchProvider;
    }

    if (scopeKind === SearchScopeKind.PublicFolder) {
        const searchProvider = SearchProvider.FindItem;
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, searchProvider);
        }
        return searchProvider;
    }

    if (isAccountExplicitLogon(mailboxInfo)) {
        const searchProvider = SearchProvider.SubstrateV2;
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, searchProvider);
        }
        return searchProvider;
    }

    if (
        scopeKind === SearchScopeKind.PrimaryMailbox ||
        scopeKind === SearchScopeKind.ArchiveMailbox ||
        scopeKind === SearchScopeKind.Group
    ) {
        const searchProvider = SearchProvider.SubstrateV2;
        if (shouldWriteToCache) {
            searchProviderCache.set(searchSessionGuid, searchProvider);
        }
        return searchProvider;
    }

    // Default to 3Sv2, but do not cache it. Log unexpected instances so we can determine when this default is being hit.
    // Missing scopeKind is an expected case. When the searchbox is first clicked,
    // the scope is not yet set, so we choose a default provider in the meantime.
    if (!!scopeKind) {
        logUsage('GetSearchProvider_DefaultTo3Sv2', mailboxInfo, {
            scopeKind,
            searchSessionGuid,
            isOffline: isOffline(),
            isOfflineSyncEnabled: isOfflineSyncEnabled(getGlobalSettingsAccountMailboxInfo()),
        });
    }
    return SearchProvider.SubstrateV2;
}
